import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import Watermark from '../components/Watermark';
import TimePicker from '../components/TimePicker';

const TimeOfBirth = () => {
  const navigate = useNavigate();
  const [selectedHour, setSelectedHour] = useState(8);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState('PM');

  const handleContinue = () => {
    // Get birth date from localStorage
    const birthDate = JSON.parse(localStorage.getItem('birthDate') || '{}');
    const person_id = `user_${Date.now()}`;
    
    // Format time
    let hour24 = selectedHour;
    if (selectedPeriod === 'PM' && selectedHour !== 12) hour24 += 12;
    if (selectedPeriod === 'AM' && selectedHour === 12) hour24 = 0;
    
    const timeString = `${String(hour24).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}:00`;
    const dateString = `${birthDate.year}-${String(new Date(birthDate.month + ' 1, 2000').getMonth() + 1).padStart(2, '0')}-${String(birthDate.day).padStart(2, '0')}`;
    
    // Save to localStorage
    localStorage.setItem('currentPersonId', person_id);
    localStorage.setItem('birthTime', JSON.stringify({hour: selectedHour, minute: selectedMinute, period: selectedPeriod}));
    localStorage.setItem('birthData', JSON.stringify({
      person_id,
      name: localStorage.getItem('userName') || 'User',
      date: dateString,
      time: timeString,
      latitude: 28.6139,
      longitude: 77.2090,
      timezone: 'Asia/Kolkata',
      place_name: localStorage.getItem('placeOfBirth') || 'New Delhi'
    }));
    
    toast.success('Birth details saved! ✨');
    navigate('/verify-otp');
  };

  return (
    <CosmicBackground showAstroChart={true}>
      <Watermark position="bottom-right" opacity={0.3} />
      
      <div className="min-h-screen relative" data-testid="time-of-birth-screen">
        {/* Progress bar */}
        <div className="absolute top-14 left-0 right-0 flex gap-1 px-4" data-testid="progress-bar">
          <div className="flex-1 h-1 bg-[#D4AF37] rounded-full" />
          <div className="flex-1 h-1 bg-[#D4AF37] rounded-full" />
          <div className="flex-1 h-1 bg-gray-600 rounded-full" />
          <div className="flex-1 h-1 bg-gray-600 rounded-full" />
          <div className="flex-1 h-1 bg-gray-600 rounded-full" />
        </div>

        {/* Back button */}
        <button 
          onClick={() => navigate(-1)} 
          className="absolute top-14 left-4 text-white z-20"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="flex flex-col h-screen pt-24 px-6">
          {/* Title */}
          <h1 
            className="text-white text-3xl font-serif text-center mb-3"
            style={{letterSpacing: '0.1em'}}
            data-testid="title"
          >
            TIME OF BIRTH
          </h1>

          {/* Subtitle */}
          <p className="text-gray-400 text-center text-sm mb-12 px-4 md:px-8">
            Swipe to select your birth time
          </p>

          {/* Swipeable Time Picker */}
          <div className="flex-1 flex justify-center items-center mb-8">
            <div className="flex gap-2 items-center relative">
              {/* Hour Picker */}
              <div className="h-48 w-16 overflow-y-auto snap-y snap-mandatory" style={{scrollbarWidth: 'none'}}>
                <div className="h-20" />
                {Array.from({length: 12}, (_, i) => i + 1).map((hour) => (
                  <div
                    key={hour}
                    onClick={() => setSelectedHour(hour)}
                    className={`h-12 flex items-center justify-center snap-center cursor-pointer transition-all ${
                      hour === selectedHour ? 'text-[#D4AF37] text-3xl font-bold' : 'text-gray-500 text-xl'
                    }`}
                  >
                    {String(hour).padStart(2, '0')}
                  </div>
                ))}
                <div className="h-20" />
              </div>

              <span className="text-[#D4AF37] text-3xl font-bold">:</span>

              {/* Minute Picker */}
              <div className="h-48 w-16 overflow-y-auto snap-y snap-mandatory" style={{scrollbarWidth: 'none'}}>
                <div className="h-20" />
                {Array.from({length: 60}, (_, i) => i).map((minute) => (
                  <div
                    key={minute}
                    onClick={() => setSelectedMinute(minute)}
                    className={`h-12 flex items-center justify-center snap-center cursor-pointer transition-all ${
                      minute === selectedMinute ? 'text-[#D4AF37] text-3xl font-bold' : 'text-gray-500 text-xl'
                    }`}
                  >
                    {String(minute).padStart(2, '0')}
                  </div>
                ))}
                <div className="h-20" />
              </div>

              {/* AM/PM Toggle */}
              <div className="ml-4 flex flex-col gap-2">
                {['AM', 'PM'].map((period) => (
                  <button
                    key={period}
                    onClick={() => setSelectedPeriod(period)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      period === selectedPeriod
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-white/10 text-gray-400'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>

              {/* Selection indicator */}
              <div className="absolute left-0 right-20 h-12 border-y-2 border-[#D4AF37] pointer-events-none" style={{top: 'calc(50% - 24px)'}} />
            </div>
          </div>

          {/* Selected Time Display */}
          <div className="text-center mb-8">
            <p className="text-white text-xl font-semibold">
              {String(selectedHour).padStart(2, '0')}:{String(selectedMinute).padStart(2, '0')} {selectedPeriod}
            </p>
          </div>

          {/* Continue button */}
          <button
            onClick={handleContinue}
            className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-[#0a1628] rounded-full font-semibold text-lg flex items-center justify-center gap-2 mb-8 hover:scale-105 transition-transform shadow-lg"
            data-testid="continue-btn"
          >
            Continue
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Bottom indicator */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 bg-white/30 rounded-full" />
      </div>
    </CosmicBackground>
  );
};

export default TimeOfBirth;
